import React, { useState } from 'react';
import { DataSet, PrimaryDataSet, DerivedDataSet } from '../../types/dataset.types';
import { Dialog } from './common/Dialog';
import { Button } from './common/Button';
import { useProjectStore } from '../stores/projectStore';
import * as styles from '../styles/components/dataset-metadata-modal.css.ts';

interface DatasetMetadataModalProps {
  dataset: DataSet;
  onClose: () => void;
}

/**
 * DatasetMetadataModal
 *
 * データセットのメタデータを表示・編集するモーダルダイアログ
 */
export function DatasetMetadataModal({ dataset, onClose }: DatasetMetadataModalProps) {
  const updateDataset = useProjectStore((state) => state.updateDataset);
  const [description, setDescription] = useState(dataset.metadata?.description || '');
  const [hasChanges, setHasChanges] = useState(false);

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(e.target.value);
    setHasChanges(e.target.value !== (dataset.metadata?.description || ''));
  };

  const handleSave = () => {
    if (dataset.type === 'derived') {
      updateDataset(dataset.id, (d) => {
        const derived = d as DerivedDataSet;
        return {
          ...derived,
          metadata: {
            ...derived.metadata,
            description: description || undefined,
          },
        };
      });
    } else if (dataset.type === 'primary') {
      updateDataset(dataset.id, (d) => {
        const primary = d as PrimaryDataSet;
        return {
          ...primary,
          metadata: {
            ...primary.metadata,
            description: description || undefined,
          },
        };
      });
    }
    setHasChanges(false);
    onClose();
  };

  return (
    <Dialog
      isOpen={true}
      onClose={onClose}
      title="Dataset Metadata"
      width="md"
      data-testid="dataset-metadata-modal"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save
          </Button>
        </>
      }
    >
      <div className={styles.content}>
          {/* 基本情報 */}
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Basic Information</h3>
            <div className={styles.row}>
              <span className={styles.label}>Name:</span>
              <span className={styles.value}>{dataset.name}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Type:</span>
              <span className={styles.value}>{dataset.type}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Rows:</span>
              <span className={styles.value}>{dataset.rowCount?.toLocaleString() || 'N/A'}</span>
            </div>
            <div className={styles.row}>
              <span className={styles.label}>Columns:</span>
              <span className={styles.value}>{Object.keys(dataset.schema.columns).length}</span>
            </div>
          </div>

          {/* PrimaryDataSetのメタデータ */}
          {dataset.type === 'primary' && (() => {
            const primaryDataset = dataset as PrimaryDataSet;
            return (
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Source Information</h3>
                <div className={styles.row}>
                  <span className={styles.label}>Source File:</span>
                  <span className={styles.value}>{primaryDataset.metadata.originalFileName}</span>
                </div>
                <div className={styles.row}>
                  <span className={styles.label}>Imported:</span>
                  <span className={styles.value}>{new Date(primaryDataset.metadata.importedAt).toLocaleString()}</span>
                </div>
                {primaryDataset.metadata.fileSize && (
                  <div className={styles.row}>
                    <span className={styles.label}>File Size:</span>
                    <span className={styles.value}>{(primaryDataset.metadata.fileSize / 1024).toFixed(1)} KB</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* DerivedDataSetのメタデータ */}
          {dataset.type === 'derived' && (() => {
            const derivedDataset = dataset as DerivedDataSet;
            return (
              <>
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Derivation Information</h3>
                  <div className={styles.row}>
                    <span className={styles.label}>Created:</span>
                    <span className={styles.value}>{new Date(derivedDataset.metadata.createdAt).toLocaleString()}</span>
                  </div>
                  <div className={styles.row}>
                    <span className={styles.label}>Operation:</span>
                    <span className={styles.value}>{derivedDataset.operation.type.replace('_', ' ')}</span>
                  </div>
                </div>
              </>
            );
          })()}

          {/* Description（Primary/Derivedで編集可能） */}
          {(dataset.type === 'primary' || dataset.type === 'derived') && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Description</h3>
              <textarea
                className={styles.descriptionTextarea}
                value={description}
                onChange={handleDescriptionChange}
                placeholder="Add a description for this dataset..."
                rows={3}
                data-testid="description-textarea"
              />
            </div>
          )}

          {/* EphemeralDataSetのメタデータ */}
          {dataset.type === 'ephemeral' && dataset.metadata && (
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Ephemeral Dataset Information</h3>
              {dataset.metadata.createdAt && (
                <div className={styles.row}>
                  <span className={styles.label}>Created:</span>
                  <span className={styles.value}>{new Date(dataset.metadata.createdAt).toLocaleString()}</span>
                </div>
              )}
              {dataset.metadata.purpose && (
                <div className={styles.row}>
                  <span className={styles.label}>Purpose:</span>
                  <span className={styles.value}>{dataset.metadata.purpose}</span>
                </div>
              )}
              {dataset.metadata.description && (
                <div className={styles.row}>
                  <span className={styles.label}>Description:</span>
                  <span className={styles.value}>{dataset.metadata.description}</span>
                </div>
              )}
            </div>
          )}
        </div>
    </Dialog>
  );
}
